import { useState, useEffect, useRef } from 'react';

const SocialMediaServices = () => {
  const services = [
    {
      icon: 'ri-palette-line',
      title: 'Content Creation',
      description: 'Professionelle Erstellung von Posts, Stories und Videos, die Ihre Marke perfekt repräsentieren.',
      features: [
        'Grafik Design',
        'Video Produktion',
        'Copywriting',
        'Brand Storytelling'
      ]
    },
    {
      icon: 'ri-calendar-line',
      title: 'Content Planning',
      description: 'Strategische Planung und Scheduling Ihrer Social Media Inhalte für maximale Reichweite.',
      features: [
        'Content Kalender',
        'Posting Schedule',
        'Trend Analysis',
        'Hashtag Research'
      ]
    },
    {
      icon: 'ri-bar-chart-line',
      title: 'Analytics & Reporting',
      description: 'Detaillierte Auswertung Ihrer Social Media Performance mit actionable Insights.',
      features: [
        'Performance Tracking',
        'Audience Insights',
        'Competitor Analysis',
        'ROI Measurement'
      ]
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Community Management',
      description: 'Professionelle Betreuung Ihrer Community mit schnellen Reaktionszeiten.',
      features: [
        'Comment Management',
        'Direct Messages',
        'Crisis Management',
        'Customer Support'
      ]
    },
    {
      icon: 'ri-advertisement-line',
      title: 'Paid Advertising',
      description: 'Gezielte Werbekampagnen auf allen relevanten Social Media Plattformen.',
      features: [
        'Facebook Ads',
        'Instagram Ads',
        'TikTok Ads',
        'LinkedIn Ads'
      ]
    },
    {
      icon: 'ri-search-line',
      title: 'Social Media SEO',
      description: 'Optimierung Ihrer Profile und Inhalte für bessere Auffindbarkeit.',
      features: [
        'Profile Optimization',
        'Hashtag Strategy',
        'Cross-Platform SEO'
      ]
    }
  ];

  return (
    <section id="services" className="py-24 bg-black">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="flex items-center justify-center space-x-3 text-[#d1bc8a] mb-6">
            <div className="w-2 h-2 bg-[#d1bc8a] rounded-full animate-pulse"></div>
            <span className="text-sm uppercase tracking-wider">Unsere Services</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Social Media
            <span className="block text-[#d1bc8a]">
              Plattformen
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Professionelle Betreuung für alle wichtigen Social Media Kanäle. 
            Maßgeschneiderte Strategien für maximale Reichweite und Engagement.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Instagram Marketing */}
          <div className="group relative bg-zinc-900 rounded-2xl p-8 border border-zinc-800 hover:border-[#d1bc8a]/50 transition-all duration-500 hover:scale-105 overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute top-4 right-4 w-20 h-20 bg-[#d1bc8a]/10 rounded-full blur-xl"></div>
            <div className="absolute bottom-4 left-4 w-16 h-16 bg-[#d1bc8a]/10 rounded-full blur-lg"></div>
            
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#d1bc8a]/10 to-transparent -skew-x-12 transform translate-x-[-100%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
            
            <div className="relative z-10">
              {/* Icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform duration-300">
                <i className="ri-instagram-line text-white text-2xl"></i>
              </div>

              {/* Content */}
              <h3 className="text-2xl font-bold text-white mb-4">Instagram Marketing</h3>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Visuelles Storytelling, das begeistert. Von Feed-Optimierung bis zu Story-Strategien.
              </p>

              {/* Features */}
              <ul className="space-y-4">
                <li className="flex items-center text-gray-400">
                  <div className="w-2 h-2 bg-[#d1bc8a] rounded-full mr-3"></div>
                  Content Erstellung & Planung
                </li>
                <li className="flex items-center text-gray-400">
                  <div className="w-2 h-2 bg-[#d1bc8a] rounded-full mr-3"></div>
                  Community Management
                </li>
                <li className="flex items-center text-gray-400">
                  <div className="w-2 h-2 bg-[#d1bc8a] rounded-full mr-3"></div>
                  Social Media Advertising
                </li>
                <li className="flex items-center text-gray-400">
                  <div className="w-2 h-2 bg-[#d1bc8a] rounded-full mr-3"></div>
                  Analytics & Reporting
                </li>
              </ul>

              {/* CTA Button */}
              <button className="w-full bg-[#d1bc8a] hover:bg-[#c4af7d] text-black py-3 rounded-xl font-semibold transition-all duration-300 opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 cursor-pointer whitespace-nowrap mt-6">
                Mehr erfahren
              </button>
            </div>
          </div>

          {/* Facebook Marketing */}
          <div className="group relative bg-zinc-900 rounded-2xl p-8 border border-zinc-800 hover:border-[#d1bc8a]/50 transition-all duration-500 hover:scale-105 overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute top-4 right-4 w-20 h-20 bg-[#d1bc8a]/10 rounded-full blur-xl"></div>
            <div className="absolute bottom-4 left-4 w-16 h-16 bg-[#d1bc8a]/10 rounded-full blur-lg"></div>
            
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#d1bc8a]/10 to-transparent -skew-x-12 transform translate-x-[-100%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
            
            <div className="relative z-10">
              {/* Icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform duration-300">
                <i className="ri-facebook-fill text-white text-2xl"></i>
              </div>

              {/* Content */}
              <h3 className="text-2xl font-bold text-white mb-4">Facebook Marketing</h3>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Reichweitenstarke Kampagnen für alle Zielgruppen. Präzises Targeting und messbare Erfolge.
              </p>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-gray-400">
                  <div className="w-2 h-2 bg-[#d1bc8a] rounded-full mr-3"></div>
                  Facebook Ads Kampagnen
                </li>
                <li className="flex items-center text-gray-400">
                  <div className="w-2 h-2 bg-[#d1bc8a] rounded-full mr-3"></div>
                  Zielgruppen-Targeting
                </li>
                <li className="flex items-center text-gray-400">
                  <div className="w-2 h-2 bg-[#d1bc8a] rounded-full mr-3"></div>
                  Community Management
                </li>
                <li className="flex items-center text-gray-400">
                  <div className="w-2 h-2 bg-[#d1bc8a] rounded-full mr-3"></div>
                  Performance Tracking
                </li>
              </ul>

              {/* CTA Button */}
              <button className="w-full bg-[#d1bc8a] hover:bg-[#c4af7d] text-black py-3 rounded-xl font-semibold transition-all duration-300 opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 cursor-pointer whitespace-nowrap">
                Mehr erfahren
              </button>
            </div>
          </div>

          {/* TikTok Marketing */}
          <div className="group relative bg-zinc-900 rounded-2xl p-8 border border-zinc-800 hover:border-[#d1bc8a]/50 transition-all duration-500 hover:scale-105 overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute top-4 right-4 w-20 h-20 bg-[#d1bc8a]/10 rounded-full blur-xl"></div>
            <div className="absolute bottom-4 left-4 w-16 h-16 bg-[#d1bc8a]/10 rounded-full blur-lg"></div>
            
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#d1bc8a]/10 to-transparent -skew-x-12 transform translate-x-[-100%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
            
            <div className="relative z-10">
              {/* Icon */}
              <div className="w-16 h-16 bg-[#d1bc8a] rounded-xl flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform duration-300">
                <i className="ri-tiktok-fill text-black text-2xl"></i>
              </div>

              {/* Content */}
              <h3 className="text-2xl font-bold text-white mb-4">TikTok Marketing</h3>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Viral gehen mit der richtigen Strategie. Trends erkennen, Content erstellen, Reichweite maximieren.
              </p>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-gray-400">
                  <div className="w-2 h-2 bg-[#d1bc8a] rounded-full mr-3"></div>
                  Viral Content Strategien
                </li>
                <li className="flex items-center text-gray-400">
                  <div className="w-2 h-2 bg-[#d1bc8a] rounded-full mr-3"></div>
                  Trend-Analyse & Umsetzung
                </li>
                <li className="flex items-center text-gray-400">
                  <div className="w-2 h-2 bg-[#d1bc8a] rounded-full mr-3"></div>
                  TikTok Ads Kampagnen
                </li>
                <li className="flex items-center text-gray-400">
                  <div className="w-2 h-2 bg-[#d1bc8a] rounded-full mr-3"></div>
                  Creator Partnerships
                </li>
              </ul>

              {/* CTA Button */}
              <button className="w-full bg-[#d1bc8a] hover:bg-[#c4af7d] text-black py-3 rounded-xl font-semibold transition-all duration-300 opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 cursor-pointer whitespace-nowrap">
                Mehr erfahren
              </button>
            </div>
          </div>
        </div>


      </div>
    </section>
  );
};

export default SocialMediaServices;
